import Icon from "./Icon.jsx";

/**
 * Chip Component with style logic strictly matching the provided Django Python definition.
 * * @param {object} props
 * @param {string} props.label - The text displayed inside the chip.
 * @param {'sm' | 'md'} [props.size='md'] - The size of the chip.
 * @param {boolean} [props.selected=false] - Whether the chip appears selected. (was "yes"/"no")
 * @param {boolean} [props.showCloseButton=false] - Whether to show a close button. (was "True"/"False")
 * @param {function} [props.onClick] - Click handler for the chip body.
 * @param {function} [props.onClickCloseButton] - Click handler for the close button.
 */

export default function Chip({
    label,
    size = 'sm',
    selected = false,
    showCloseButton = false,
    onClick,
    onClickCloseButton
}) {
    const TEXT_SIZE_MAP = {
        "sm": "font-body text-caption",
        "md": "font-header text-body-small",
    };

    const ICON_SIZE_MAP = {
        "sm": "xs",
        "md": "sm",
    };

    // Calculate style_class based on 'selected' prop
    let styleClass = '';
    const isSelected = selected === true;

    if (isSelected) {
        styleClass = "bg-main-background-accented-secondary";
    } else {
        styleClass = "bg-white border border-main-border-accented-secondary hover:border-main-typography-accented-secondary";
    }

    // Calculate show_showCloseButton based on conditional logic
    // show_showCloseButton = (selected == "yes") and (showCloseButton == "True")
    const shouldShowCloseButton = isSelected && showCloseButton === true;



    // --- 2. Class Resolution ---

    const textClass = TEXT_SIZE_MAP[size] || "font-body text-caption";
    const closeIconSize = ICON_SIZE_MAP[size] || "xs";

    // Determine if the chip body is clickable (used for hover cursor)
    const clickableClass = onClick ? 'cursor-pointer' : '';

    // Final classes applied to the main <span> element
    // Note: px-3 py-2 is from the template HTML, not the Python logic
    const chipClass = `inline-flex items-center rounded-lg px-3 py-2 ${styleClass} ${clickableClass}`;

    // Final classes applied to the text <span> element
    const labelClass = `relative inline-block px-2 text-main-typography-accented-secondary font-ui ${textClass}`;

    function renderIcon({ iconName, onClick }) {
        if (!iconName) return null;
        return <span
            className="ml-1 inline-flex items-center justify-center p-0 m-0" // Zero padding/margin, just the ml-1 spacing
            onClick={onClick}
            role="button" // Improves accessibility for screen readers
        >
            <Icon name={iconName} size={closeIconSize} className="text-main-icon-accented-secondary" />
        </span>

    }

    const CloseIcon = () => renderIcon({ iconName: 'x', onClick: onClickCloseButton });

    // --- 3. Render ---
    return (
        <span
            className={chipClass}
            onClick={onClick}
        >
            {/* Label - Uses {{ text_class }} logic */}
            <span className={labelClass}>
                {label}
            </span>
            {shouldShowCloseButton && (<CloseIcon />)}
        </span>
    );
};